import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { X, Eye, Download, Settings, Clock, User, Activity, ChevronDown, Trash2, CheckCircle } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import './ProposalActivityTracker.css';

interface ActivityEvent {
    id: string;
    type: 'view' | 'download' | 'species_change' | 'install_toggle' | 'signed';
    title: string;
    description: string;
    timestamp: Date;
    metadata?: Record<string, any>;
    actorType?: 'client' | 'employee';
    actorName?: string;
}

interface ProposalActivityTrackerProps {
    dealId: string;
    proposalId?: string;
    clientName: string;
    onClose: () => void;
}

const ProposalActivityTracker: React.FC<ProposalActivityTrackerProps> = ({
    dealId,
    proposalId,
    clientName,
    onClose,
}) => {
    const [activities, setActivities] = useState<ActivityEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalViews: 0,
        totalDownloads: 0,
        lastViewed: null as Date | null,
        lastDownloaded: null as Date | null,
    });
    const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState<'all' | 'client' | 'employee'>('all');

    const toggleEventExpanded = (id: string) => {
        setExpandedEvents(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    useEffect(() => {
        const fetchActivities = async () => {
            setLoading(true);
            try {
                const events: ActivityEvent[] = [];

                // Fetch proposal activities from 'activities' collection
                // Simple query - filter by entityId only, sort client-side to avoid index
                const activitiesRef = collection(db, 'activities');
                const activitiesQuery = query(
                    activitiesRef,
                    where('entityId', '==', dealId)
                );

                const clientTitles = ['Proposal Viewed', 'Proposal Downloaded', 'Species Changed', 'Kit Species Changed', 'Install Toggled', 'Proposal Signed!'];
                const employeePrefixes = ['Proposal sent:', 'Proposal unsent:'];

                const isRelevant = (title: string) => {
                    if (clientTitles.includes(title)) return true;
                    return employeePrefixes.some(prefix => title.startsWith(prefix));
                };

                const activitiesSnapshot = await getDocs(activitiesQuery);
                activitiesSnapshot.forEach((doc) => {
                    const data = doc.data();

                    // Filter to only proposal-related activities
                    if (!isRelevant(data.title)) return;

                    const timestamp = data.createdAt?.toDate?.() || new Date();
                    let type: ActivityEvent['type'] = 'view';

                    if (data.title === 'Proposal Downloaded') type = 'download';
                    else if (data.title === 'Species Changed' || data.title === 'Kit Species Changed') type = 'species_change';
                    else if (data.title === 'Install Toggled') type = 'install_toggle';
                    else if (data.title === 'Proposal Signed!') type = 'signed';

                    events.push({
                        id: doc.id,
                        type,
                        title: data.title,
                        description: data.description || '',
                        timestamp,
                        metadata: data,
                        actorType: data.actorType || (data.createdBy === 'system' ? 'client' : 'employee'),
                        actorName: data.actorName || (data.createdBy === 'system' ? data.entityName || 'Client' : data.createdBy),
                    });
                });

                // Fetch downloads from 'proposalDownloads' collection for more detail
                if (proposalId) {
                    const downloadsRef = collection(db, 'proposalDownloads');
                    const downloadsQuery = query(
                        downloadsRef,
                        where('proposalId', '==', proposalId),
                        orderBy('downloadedAt', 'desc')
                    );

                    const downloadsSnapshot = await getDocs(downloadsQuery);
                    downloadsSnapshot.forEach((doc) => {
                        const data = doc.data();
                        const timestamp = data.downloadedAt?.toDate?.() || new Date();

                        // Only add if not already in activities (avoid duplicates)
                        const exists = events.some(e =>
                            e.type === 'download' &&
                            Math.abs(e.timestamp.getTime() - timestamp.getTime()) < 60000
                        );

                        if (!exists) {
                            events.push({
                                id: `download-${doc.id}`,
                                type: 'download',
                                title: 'PDF Downloaded',
                                description: `Downloaded for ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(data.totalAmount || 0)}`,
                                timestamp,
                                metadata: data,
                            });
                        }
                    });
                }

                // Sort by timestamp descending
                events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
                setActivities(events);

                // Calculate stats
                const views = events.filter(e => e.type === 'view');
                const downloads = events.filter(e => e.type === 'download');

                setStats({
                    totalViews: views.length,
                    totalDownloads: downloads.length,
                    lastViewed: views[0]?.timestamp || null,
                    lastDownloaded: downloads[0]?.timestamp || null,
                });

            } catch (error) {
                console.error('Error fetching activities:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchActivities();
    }, [dealId, proposalId]);

    // Filter activities based on active tab
    const filteredActivities = activities.filter(e => {
        if (activeTab === 'all') return true;
        return e.actorType === activeTab;
    });

    // Recalculate stats for filtered activities
    const filteredStats = {
        totalViews: filteredActivities.filter(e => e.type === 'view').length,
        totalDownloads: filteredActivities.filter(e => e.type === 'download').length,
        lastViewed: filteredActivities.filter(e => e.type === 'view')[0]?.timestamp || null,
        lastDownloaded: filteredActivities.filter(e => e.type === 'download')[0]?.timestamp || null,
    };

    const getEventIcon = (type: ActivityEvent['type']) => {
        switch (type) {
            case 'view': return <Eye size={16} />;
            case 'download': return <Download size={16} />;
            case 'species_change': return <Settings size={16} />;
            case 'install_toggle': return <Settings size={16} />;
            case 'signed': return <CheckCircle size={16} />;
            default: return <Activity size={16} />;
        }
    };

    const getEventColor = (type: ActivityEvent['type']) => {
        switch (type) {
            case 'view': return 'event-view';
            case 'download': return 'event-download';
            case 'species_change': return 'event-config';
            case 'install_toggle': return 'event-config';
            case 'signed': return 'event-signed';
            default: return '';
        }
    };

    const [resetting, setResetting] = useState(false);

    const resetActivity = async () => {
        if (!window.confirm('Are you sure you want to clear all activity for this proposal? This cannot be undone.')) {
            return;
        }

        setResetting(true);
        try {
            // Delete activities from activities collection
            const activitiesRef = collection(db, 'activities');
            const activitiesQuery = query(
                activitiesRef,
                where('entityId', '==', dealId)
            );

            const validTitles = ['Proposal Viewed', 'Proposal Downloaded', 'Species Changed', 'Install Toggled', 'Proposal Signed!'];
            const activitiesSnapshot = await getDocs(activitiesQuery);
            const deletePromises: Promise<void>[] = [];

            activitiesSnapshot.forEach((docSnapshot) => {
                const data = docSnapshot.data();
                if (validTitles.includes(data.title)) {
                    deletePromises.push(deleteDoc(doc(db, 'activities', docSnapshot.id)));
                }
            });

            // Delete from proposalDownloads collection
            if (proposalId) {
                const downloadsRef = collection(db, 'proposalDownloads');
                const downloadsQuery = query(
                    downloadsRef,
                    where('proposalId', '==', proposalId)
                );
                const downloadsSnapshot = await getDocs(downloadsQuery);
                downloadsSnapshot.forEach((docSnapshot) => {
                    deletePromises.push(deleteDoc(doc(db, 'proposalDownloads', docSnapshot.id)));
                });
            }

            await Promise.all(deletePromises);

            // Clear local state
            setActivities([]);
            setStats({
                totalViews: 0,
                totalDownloads: 0,
                lastViewed: null,
                lastDownloaded: null,
            });
        } catch (error) {
            console.error('Error resetting activity:', error);
            alert('Failed to reset activity. Please try again.');
        } finally {
            setResetting(false);
        }
    };

    return (
        <div className="activity-tracker-overlay" onClick={onClose}>
            <div className="activity-tracker-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="tracker-header">
                    <div className="tracker-title">
                        <Activity size={20} />
                        <h2>Activity Tracker</h2>
                    </div>
                    <div className="tracker-header-actions">
                        <button
                            className="reset-activity-btn"
                            onClick={resetActivity}
                            disabled={resetting || activities.length === 0}
                            title="Clear all activity"
                        >
                            <Trash2 size={16} />
                            {resetting ? 'Resetting...' : 'Reset'}
                        </button>
                        <button className="tracker-close" onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Client Info */}
                <div className="tracker-client">
                    <User size={16} />
                    <span>{clientName || 'Client'}</span>
                </div>

                {/* Actor Filter Tabs */}
                <div className="tracker-filter-tabs">
                    <button
                        className={`filter-tab ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all')}
                    >
                        All ({activities.length})
                    </button>
                    <button
                        className={`filter-tab ${activeTab === 'client' ? 'active' : ''}`}
                        onClick={() => setActiveTab('client')}
                    >
                        Client ({activities.filter(e => e.actorType === 'client').length})
                    </button>
                    <button
                        className={`filter-tab ${activeTab === 'employee' ? 'active' : ''}`}
                        onClick={() => setActiveTab('employee')}
                    >
                        Employee ({activities.filter(e => e.actorType === 'employee').length})
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="tracker-stats">
                    <div className="stat-card">
                        <Eye size={20} />
                        <div className="stat-info">
                            <span className="stat-value">{filteredStats.totalViews}</span>
                            <span className="stat-label">Views</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <Download size={20} />
                        <div className="stat-info">
                            <span className="stat-value">{filteredStats.totalDownloads}</span>
                            <span className="stat-label">Downloads</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <Clock size={20} />
                        <div className="stat-info">
                            <span className="stat-value">
                                {filteredStats.lastViewed ? formatDistanceToNow(filteredStats.lastViewed, { addSuffix: true }) : 'Never'}
                            </span>
                            <span className="stat-label">Last Viewed</span>
                        </div>
                    </div>
                </div>

                {/* Activity Timeline */}
                <div className="tracker-timeline">
                    <h3>Activity Timeline</h3>
                    {loading ? (
                        <div className="timeline-loading">
                            <div className="loading-spinner" />
                            <span>Loading activity...</span>
                        </div>
                    ) : filteredActivities.length === 0 ? (
                        <div className="timeline-empty">
                            <Activity size={32} />
                            <p>No {activeTab !== 'all' ? `${activeTab} ` : ''}activity recorded yet</p>
                            <span>Activity will appear here when {activeTab === 'employee' ? 'employees' : 'clients'} interact with the proposal</span>
                        </div>
                    ) : (
                        <div className="timeline-list">
                            {filteredActivities.map((event) => {
                                const hasDetails = event.metadata?.changeDetails;
                                const isExpanded = expandedEvents.has(event.id);
                                return (
                                    <div
                                        key={event.id}
                                        className={`timeline-event ${getEventColor(event.type)} ${hasDetails ? 'has-details' : ''} ${isExpanded ? 'expanded' : ''}`}
                                        onClick={() => hasDetails && toggleEventExpanded(event.id)}
                                    >
                                        <div className="event-icon">
                                            {getEventIcon(event.type)}
                                        </div>
                                        <div className="event-content">
                                            <div className="event-header">
                                                <span className="event-title">{event.title}</span>
                                                <div className="event-header-right">
                                                    <span className="event-time">
                                                        {formatDistanceToNow(event.timestamp, { addSuffix: true })}
                                                    </span>
                                                    {hasDetails && (
                                                        <ChevronDown
                                                            size={14}
                                                            className={`expand-icon ${isExpanded ? 'rotated' : ''}`}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            {event.actorName && (
                                                <span className={`actor-badge ${event.actorType === 'employee' ? 'actor-employee' : 'actor-client'}`}>
                                                    {event.actorType === 'employee' ? '👤' : '🧑‍💼'} {event.actorName}
                                                </span>
                                            )}
                                            {event.description && (
                                                <p className="event-description">{event.description}</p>
                                            )}
                                            {isExpanded && hasDetails && event.metadata?.changeDetails && (() => {
                                                const cd = event.metadata.changeDetails;
                                                return (
                                                    <div className="event-details">
                                                        {cd.structureName && (
                                                            <div className="detail-row">
                                                                <span className="detail-label">Structure:</span>
                                                                <span className="detail-value">{cd.structureName}</span>
                                                            </div>
                                                        )}
                                                        {cd.previousSpecies && (
                                                            <div className="detail-row">
                                                                <span className="detail-label">From:</span>
                                                                <span className="detail-value">{cd.previousSpecies}</span>
                                                            </div>
                                                        )}
                                                        {cd.newSpecies && (
                                                            <div className="detail-row">
                                                                <span className="detail-label">To:</span>
                                                                <span className="detail-value">{cd.newSpecies}</span>
                                                            </div>
                                                        )}
                                                        {cd.previousValue && (
                                                            <div className="detail-row">
                                                                <span className="detail-label">Changed:</span>
                                                                <span className="detail-value">
                                                                    {cd.previousValue} → {cd.newValue}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            <span className="event-date">
                                                {format(event.timestamp, 'MMM d, yyyy h:mm a')}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProposalActivityTracker;
